import { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import api from './services/api';
import 'react-toastify/dist/ReactToastify.css';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import ColorModeContext from './layout/themeContext';
import { ActiveMenuContext } from './context/ActiveMenuContext';
import FaviconUpdater from './components/FaviconUpdater';
import CookieProblemModal from './components/CookieProblemModal';
import PwaUpdateBanner from './components/PwaUpdateBanner';
import { getBackendUrl } from './config';
import Routes from './routes';
import defaultLogoLight from './assets/logo.png';
import defaultLogoDark from './assets/logo-black.png';
import defaultLogoFavicon from './assets/favicon.ico';
import useSettings from './hooks/useSettings';
import { ToastContainer } from 'react-toastify';
import {
  WHITE_LABEL_THEME_KEYS,
  buildWhitelabelThemeValues,
  DEFAULT_BRAND_COLOR,
} from './utils/whitelabelTheme';
import { resolveCalculatedLogoDark, resolveCalculatedLogoLight } from './utils/whitelabelLogos';
import { DEFAULT_APP_DISPLAY_NAME } from './constants/appBrandingDefaults';
import { appCssBaselineGlobals, appTypography } from './styles/typography';

const THEME_CACHE_KEY = 'wl-theme-cache';

const readThemeCache = () => {
  try {
    const raw = localStorage.getItem(THEME_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeThemeCache = (data) => {
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(data));
  } catch {
    console.error('Error writing theme cache', data);
  }
};

const ActiveMenuProvider = ({ children }) => {
  const [activeMenu, setActiveMenu] = useState('');
  return (
    <ActiveMenuContext.Provider value={{ activeMenu, setActiveMenu }}>
      {children}
    </ActiveMenuContext.Provider>
  );
};

const App = () => {
  const isValidColor = (val) => {
    if (typeof val !== 'string') return false;
    const v = val.trim();
    return /^(#([0-9a-fA-F]{3}){1,2}|rgb\(|rgba\(|hsl\(|hsla\()/.test(v);
  };
  const sanitizeColor = (val, fallback = DEFAULT_BRAND_COLOR) =>
    isValidColor(val) ? val.trim() : fallback;
  const getContrastTextColor = (hexColor) => {
    const color = String(hexColor || '')
      .replace('#', '')
      .trim();
    const normalized =
      color.length === 3
        ? color
            .split('')
            .map((c) => c + c)
            .join('')
        : color;

    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return '#ffffff';

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.65 ? '#111111' : '#ffffff';
  };

  const [locale, _setLocale] = useState();
  // Lê cache de tema de forma síncrona: primeiro render já usa valores cacheados (sem flash)
  const themeCache = useMemo(readThemeCache, []);
  const appColorLocalStorage =
    themeCache?.primaryColorLight ||
    themeCache?.primaryColorDark ||
    localStorage.getItem('primaryColorLight') ||
    localStorage.getItem('primaryColorDark') ||
    DEFAULT_BRAND_COLOR;
  const appNameLocalStorage =
    themeCache?.appName ||
    localStorage.getItem('appName') ||
    `${DEFAULT_APP_DISPLAY_NAME} Developer`;
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const preferredTheme = window.localStorage.getItem('preferredTheme');
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? 'dark' : 'light'
  );
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(
    themeCache?.primaryColorDark || appColorLocalStorage
  );
  const [appLogoLight, setAppLogoLight] = useState(() => {
    if (themeCache?.appLogoLight) return getBackendUrl() + '/public/' + themeCache.appLogoLight;
    return defaultLogoLight;
  });
  const [appLogoDark, setAppLogoDark] = useState(() => {
    if (themeCache?.appLogoDark) return getBackendUrl() + '/public/' + themeCache.appLogoDark;
    return defaultLogoDark;
  });
  const [appLogoFavicon, setAppLogoFavicon] = useState(() => {
    if (themeCache?.appLogoFavicon) {
      const base = getBackendUrl() + '/public/' + themeCache.appLogoFavicon;
      return `${base}?v=${Date.now()}`;
    }
    return defaultLogoFavicon;
  });
  const [appName, setAppName] = useState(appNameLocalStorage);
  const [loginBackgroundImage, setLoginBackgroundImage] = useState('');
  const [cadastroBackgroundImage, setCadastroBackgroundImage] = useState('');
  const [whitelabelThemeSettings, setWhitelabelThemeSettings] = useState(() => {
    if (!themeCache) return {};
    return WHITE_LABEL_THEME_KEYS.reduce((acc, key) => {
      if (themeCache[key]) acc[key] = themeCache[key];
      return acc;
    }, {});
  });
  // false quando não há cache; true imediatamente se cache existe (sem skeleton no segundo carregamento)
  const [themeReady, setThemeReady] = useState(!!themeCache);
  const { getPublicSetting } = useSettings();

  const resolvedWhitelabelTheme = useMemo(
    () =>
      buildWhitelabelThemeValues({
        settings: whitelabelThemeSettings,
        primaryColorLight,
      }),
    [whitelabelThemeSettings, primaryColorLight]
  );

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          window.localStorage.setItem('preferredTheme', newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      setWhitelabelThemeSetting: (key, value) => {
        setWhitelabelThemeSettings((prev) => ({ ...prev, [key]: value }));
      },
      setWhitelabelThemePatch: (patch = {}) => {
        setWhitelabelThemeSettings((prev) => ({ ...prev, ...patch }));
      },
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      whitelabelThemeSettings,
      mode,
      themeReady,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode, whitelabelThemeSettings, themeReady]
  );

  const theme = useMemo(() => {
    const resolvedPrimaryColor = sanitizeColor(
      mode === 'light' ? primaryColorLight : primaryColorDark
    );
    const dialogTitleTextColor = getContrastTextColor(resolvedPrimaryColor);

    return createTheme(
      {
        typography: {
          ...appTypography,
          allVariants: {
            color: resolvedWhitelabelTheme.appTextColor,
          },
        },
        scrollbarStyles: {
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
            backgroundColor: mode === 'light' ? primaryColorLight : primaryColorDark,
          },
        },
        scrollbarStylesSoft: {
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            backgroundColor: mode === 'light' ? '#F3F3F3' : '#333333',
          },
        },
        palette: {
          type: mode,
          primary: {
            main: resolvedPrimaryColor,
          },
          textPrimary: resolvedPrimaryColor,
          borderPrimary: resolvedPrimaryColor,
          dark: { main: mode === 'light' ? '#333333' : '#F3F3F3' },
          light: { main: mode === 'light' ? '#F3F3F3' : '#333333' },
          fontColor: resolvedPrimaryColor,
          tabHeaderBackground: mode === 'light' ? '#EEE' : '#666',
          optionsBackground: mode === 'light' ? '#fafafa' : '#333',
          fancyBackground: mode === 'light' ? '#fafafa' : '#333',
          total: mode === 'light' ? '#fff' : '#222',
          messageIcons: mode === 'light' ? 'grey' : '#F3F3F3',
          inputBackground: mode === 'light' ? '#FFFFFF' : '#333',
          barraSuperior: mode === 'light' ? resolvedPrimaryColor : '#666',
        },
        props: {
          MuiSelect: {
            MenuProps: {
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
            },
          },
        },
        overrides: {
          MuiCssBaseline: {
            '@global': appCssBaselineGlobals,
          },
          MuiButton: {
            containedPrimary: {
              backgroundColor: resolvedWhitelabelTheme.buttonPrimaryBgColor,
              color: resolvedWhitelabelTheme.buttonPrimaryTextColor,
              '&:hover': {
                backgroundColor: resolvedWhitelabelTheme.buttonPrimaryHoverBgColor,
              },
            },
            textPrimary: {
              color: resolvedWhitelabelTheme.linkColor,
              '&:hover': {
                color: resolvedWhitelabelTheme.linkHoverColor,
              },
            },
          },
          MuiLink: {
            root: {
              color: resolvedWhitelabelTheme.linkColor,
              '&:hover': {
                color: resolvedWhitelabelTheme.linkHoverColor,
              },
            },
          },
          MuiTypography: {
            h1: { color: resolvedWhitelabelTheme.headingColor },
            h2: { color: resolvedWhitelabelTheme.headingColor },
            h3: { color: resolvedWhitelabelTheme.headingColor },
            h4: { color: resolvedWhitelabelTheme.headingColor },
            h5: { color: resolvedWhitelabelTheme.headingColor },
            h6: { color: resolvedWhitelabelTheme.headingColor },
            body1: { color: resolvedWhitelabelTheme.appTextColor },
            body2: { color: resolvedWhitelabelTheme.appTextColor },
          },
          MuiDialogTitle: {
            root: {
              backgroundColor: `${resolvedPrimaryColor} !important`,
              color: `${dialogTitleTextColor} !important`,
              borderBottom:
                mode === 'light'
                  ? '1px solid rgba(0, 0, 0, 0.12)'
                  : '1px solid rgba(255, 255, 255, 0.12)',
              '& .MuiTypography-root': {
                color: 'inherit !important',
                fontWeight: 600,
              },
            },
          },
        },
        mode,
        appLogoLight,
        appLogoDark,
        appLogoFavicon,
        appName,
        resolvedWhitelabelTheme,
        calculatedLogoDark: () =>
          resolveCalculatedLogoDark(appLogoLight, appLogoDark, defaultLogoLight, defaultLogoDark, {
            loginBackgroundImage,
            cadastroBackgroundImage,
          }),
        calculatedLogoLight: () =>
          resolveCalculatedLogoLight(appLogoLight, appLogoDark, defaultLogoLight, defaultLogoDark, {
            loginBackgroundImage,
            cadastroBackgroundImage,
          }),
      },
      locale
    );
  }, [
    appLogoLight,
    appLogoDark,
    appLogoFavicon,
    appName,
    locale,
    mode,
    primaryColorDark,
    primaryColorLight,
    resolvedWhitelabelTheme,
    loginBackgroundImage,
    cadastroBackgroundImage,
  ]);

  useEffect(() => {
    window.localStorage.setItem('preferredTheme', mode);
  }, [mode]);

  const applyPublicSetting = useCallback((key, value) => {
    if (value === undefined || value === null || value === '') return;
    if (key === 'primaryColorLight') setPrimaryColorLight(value || '#0000FF');
    if (key === 'primaryColorDark') setPrimaryColorDark(value || '#39ACE7');
    if (key === 'appName') setAppName(value || DEFAULT_APP_DISPLAY_NAME);
    if (key === 'appLogoLight') {
      setAppLogoLight(value ? getBackendUrl() + '/public/' + value : defaultLogoLight);
    }
    if (key === 'appLogoDark') {
      setAppLogoDark(value ? getBackendUrl() + '/public/' + value : defaultLogoDark);
    }
    if (key === 'appLogoFavicon') {
      const baseUrl = value ? getBackendUrl() + '/public/' + value : defaultLogoFavicon;
      const urlWithCacheBust = baseUrl.includes('?') ? baseUrl : `${baseUrl}?v=${Date.now()}`;
      setAppLogoFavicon(urlWithCacheBust);
    }
    if (key === 'loginBackgroundImage') setLoginBackgroundImage(value || '');
    if (key === 'cadastroBackgroundImage') setCadastroBackgroundImage(value || '');
    if (WHITE_LABEL_THEME_KEYS.includes(key)) {
      setWhitelabelThemeSettings((prev) => ({ ...prev, [key]: value }));
    }
  }, []); // todas as refs de setState são estáveis

  useEffect(() => {
    const keys = [
      'primaryColorLight',
      'primaryColorDark',
      'appName',
      'appLogoLight',
      'appLogoDark',
      'appLogoFavicon',
      'loginBackgroundImage',
      'cadastroBackgroundImage',
      ...WHITE_LABEL_THEME_KEYS,
    ];

    Promise.all(
      keys.map(async (key) => {
        try {
          const res = await getPublicSetting(key);
          const value = res?.value ?? res;
          return { key, value };
        } catch {
          return { key, value: undefined };
        }
      })
    ).then((results) => {
      const newCache = {};
      results.forEach(({ key, value }) => {
        applyPublicSetting(key, value);
        if (value !== undefined && value !== null && value !== '') {
          newCache[key] = value;
        }
      });
      writeThemeCache(newCache);
      setThemeReady(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useLayoutEffect: aplica variáveis do tema antes do paint, evitando 1 frame com cor errada (fallback azul)
  useLayoutEffect(() => {
    const root = document.documentElement;
    const resolvedPrimary = mode === 'light' ? primaryColorLight : primaryColorDark;

    root.style.setProperty('--primaryColor', resolvedPrimary);
    root.style.setProperty('--wl-menu-bg', resolvedWhitelabelTheme.menuBgColor);
    root.style.setProperty('--wl-menu-text', resolvedWhitelabelTheme.menuTextColor);
    root.style.setProperty('--wl-menu-icon', resolvedWhitelabelTheme.menuIconColor);
    root.style.setProperty('--wl-menu-header-icon', resolvedWhitelabelTheme.menuHeaderIconColor);
    root.style.setProperty('--wl-menu-hover-bg', resolvedWhitelabelTheme.menuHoverBgColor);
    root.style.setProperty('--wl-menu-hover-text', resolvedWhitelabelTheme.menuHoverTextColor);
    root.style.setProperty('--wl-menu-active-bg', resolvedWhitelabelTheme.menuActiveBgColor);
    root.style.setProperty('--wl-menu-active-text', resolvedWhitelabelTheme.menuActiveTextColor);
    root.style.setProperty(
      '--wl-menu-active-indicator',
      resolvedWhitelabelTheme.menuActiveIndicatorColor
    );
    root.style.setProperty('--wl-menu-divider', resolvedWhitelabelTheme.menuDividerColor);
    root.style.setProperty('--wl-link-color', resolvedWhitelabelTheme.linkColor);
    root.style.setProperty('--wl-link-hover-color', resolvedWhitelabelTheme.linkHoverColor);
    root.style.setProperty('--wl-btn-primary-bg', resolvedWhitelabelTheme.buttonPrimaryBgColor);
    root.style.setProperty('--wl-btn-primary-text', resolvedWhitelabelTheme.buttonPrimaryTextColor);
    root.style.setProperty(
      '--wl-btn-primary-hover-bg',
      resolvedWhitelabelTheme.buttonPrimaryHoverBgColor
    );
    root.style.setProperty('--wl-text-color', resolvedWhitelabelTheme.appTextColor);
    root.style.setProperty('--wl-heading-color', resolvedWhitelabelTheme.headingColor);
  }, [primaryColorLight, primaryColorDark, mode, resolvedWhitelabelTheme]);

  // Atualiza o título da página com o nome do sistema (e cache para o próximo carregamento)
  useEffect(() => {
    if (appName) {
      document.title = appName;
      try {
        localStorage.setItem('appName', appName);
      } catch {
        // localStorage can throw (e.g. private mode, quota); ignore
      }
    }
  }, [appName]);

  // Ao entrar no sistema: uma requisição cedo detecta "Request Header Or Cookie Too Large" (interceptor mostra a barra)
  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get('/version');
        const { data } = response;
        window.localStorage.setItem('frontendVersion', data.version);
      } catch (_error) {
        // silent fail (ex.: 400 header too large é tratado pelo interceptor → barra de cookies)
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <PwaUpdateBanner />
      <FaviconUpdater
        url={
          appLogoFavicon && appLogoFavicon !== defaultLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <CookieProblemModal />
          <ActiveMenuProvider>
            <Routes />
            <ToastContainer
              position="top-center"
              autoClose={6000}
              hideProgressBar={false}
              closeOnClick
              pauseOnHover
              draggable
              theme="light"
            />
          </ActiveMenuProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
